/**
 * Unified Search Type System
 * 
 * This file defines the single source of truth for all search-related types.
 * All search methods must return SearchResult[] in SearchResponse format.
 * 
 * Phase 1 of Search Architecture Refactor
 * Created: 2025-11-24
 */

/**
 * Token represents a parsed search input element
 * Used for structured queries like: [{type: 'term', value: 'cat'}, {type: 'color', value: 'blue'}]
 */
export interface Token {
  type?: 'term' | 'color' | 'channel' | 'type' | 'time' | 'not' | 'tag';
  value: string;
  originalValue?: string;
  metadata?: Record<string, any>;
}

/**
 * Standard search result type - SINGLE SOURCE OF TRUTH
 * 
 * All search paths (RPC, Edge Function, direct SQL) must normalize to this format.
 * This ensures consistent rendering in the UI and eliminates format conversion bugs.
 */
export interface SearchResult {
  // Core fields (always present)
  id: string;
  title: string;
  description: string | null;
  url: string;
  source_type: string;
  created_at: string;

  // Search-specific fields
  similarity: number;           // Always 0-1 range, represents relevance score
  personalized?: boolean;       // True if user context was used for ranking

  // Content fields
  meta: Record<string, any>;    // Metadata (colors, analysis, etc.)
  auto_tags: Record<string, any>; // Auto-generated tags
  raw_text: string | null;      // Extracted text content

  // Optional fields
  snippet?: string;             // Text snippet highlighting match
  image_url?: string;           // Image URL if applicable
  thumbnail_url?: string;       // Thumbnail URL if applicable
}

/**
 * Search mode indicates which search strategy was used
 */
export type SearchMode = 
  | 'semantic'          // Vector similarity search via RPC (threshold: 0.30)
  | 'semantic-soft'     // Semantic search with relaxed threshold (0.15)
  | 'semantic-nearest'  // Semantic search with no threshold (nearest neighbors)
  | 'keyword'           // Full-text search via SQL ILIKE
  | 'hybrid'            // Combination of semantic + keyword (future)
  | 'color-tag'         // Direct color tag query fallback
  | 'similar'           // Visual similarity search
  | 'none'              // Results rejected by similarity floor (< 0.15)
  | 'empty';            // No results found

/**
 * Standard search response - returned by all search methods
 */
export interface SearchResponse {
  ok: boolean;
  results: SearchResult[];
  searchMode: SearchMode;
  nextCursor: string | null;

  // Optional metadata
  refinements?: {
    terms?: string[];
    not?: string[];
    types?: string[];
    colors?: string[];
    timeRange?: { from: Date | null; to: Date | null };
  };

  meta?: {
    processingTime?: number;    // milliseconds
    resultCount?: number;        // Made optional for flexibility
    embeddingUsed?: boolean;    // True if semantic search was attempted
    userContextUsed?: boolean;  // True if user context was blended
    fallbackLevel?: number;     // Which fallback level was used (1-6)
    similarityThreshold?: number; // Threshold used for this search
    colorQuery?: string[];       // Colors searched for (Level 5)
    source?: string;             // Source table for results
    sourceItemId?: string;       // For similarity search - source item ID
    sourceItemTitle?: string;    // For similarity search - source item title
    [key: string]: any;          // Allow additional properties
  };

  error?: string;               // Error message if ok = false
}

/**
 * Search options - passed to search methods
 */
export interface SearchOptions {
  // Pagination
  limit?: number;               // Max results to return (default: 50)
  cursor?: string | null;       // Pagination cursor for next page

  // User context
  userId?: string;              // User ID for personalization

  // Filters
  filters?: {
    types?: string[];           // Filter by source_type
    colors?: string[];          // Filter by color
    channels?: string[];        // Filter by channel
    tags?: string[];            // Filter by tag
    timeRange?: {
      from: Date;
      to: Date;
    };
  };

  // Search behavior
  includeKeywordFallback?: boolean; // If true, fall back to keyword search (default: true)
  minSimilarity?: number;       // Minimum similarity threshold (default: 0.3)
  
  // Debug
  debug?: boolean;              // Enable verbose logging
}

/**
 * User context for personalized search
 * Retrieved from personalization service
 */
export interface UserContext {
  userId: string;
  embedding: number[];          // User's personalized embedding vector
  interactionCount: number;
  lastUpdated: string;
  metadata?: Record<string, any>;
}

/**
 * Embedding vector type
 * Standard OpenAI text-embedding-3-large format (1536 dimensions)
 */
export type EmbeddingVector = number[];

/**
 * RPC result format (from deepsearch_semantic_search_custom)
 * Internal type - should be normalized to SearchResult
 */
export interface RPCSearchResult {
  id: string;
  source_type: string;
  title: string;
  description: string | null;
  auto_tags: Record<string, any> | null;
  raw_text: string | null;
  created_at: string;
  updated_at: string;
  similarity: number;
  url: string;
  metadata?: Record<string, any>;  // RPC returns 'metadata' (mapped from 'meta' in SQL)
  meta?: Record<string, any>;      // Keep for backward compatibility
}

/**
 * Search error types
 */
export class SearchError extends Error {
  constructor(
    message: string,
    public code: string,
    public details?: any
  ) {
    super(message);
    this.name = 'SearchError';
  }
}

export class EmbeddingGenerationError extends SearchError {
  constructor(message: string, details?: any) {
    super(message, 'EMBEDDING_GENERATION_FAILED', details);
    this.name = 'EmbeddingGenerationError';
  }
}

export class SemanticSearchError extends SearchError {
  constructor(message: string, details?: any) {
    super(message, 'SEMANTIC_SEARCH_FAILED', details);
    this.name = 'SemanticSearchError';
  }
}

export class KeywordSearchError extends SearchError {
  constructor(message: string, details?: any) {
    super(message, 'KEYWORD_SEARCH_FAILED', details);
    this.name = 'KeywordSearchError';
  }
}

/**
 * Type guard to check if an object is a SearchResult
 */
export function isSearchResult(obj: any): obj is SearchResult {
  return (
    obj &&
    typeof obj === 'object' &&
    typeof obj.id === 'string' &&
    typeof obj.title === 'string' &&
    typeof obj.source_type === 'string' &&
    typeof obj.similarity === 'number' &&
    obj.similarity >= 0 &&
    obj.similarity <= 1
  );
}

/**
 * Type guard to check if an object is a SearchResponse
 */
export function isSearchResponse(obj: any): obj is SearchResponse {
  return (
    obj &&
    typeof obj === 'object' &&
    typeof obj.ok === 'boolean' &&
    Array.isArray(obj.results) &&
    ['semantic', 'keyword', 'hybrid', 'empty'].includes(obj.searchMode)
  );
}

/**
 * Helper type for normalizer functions
 * Phase 5: Removed 'edge' source (deprecated)
 */
export type SearchResultNormalizer<T = any> = (
  rawResult: T,
  source: 'rpc' | 'keyword'
) => SearchResult;

/**
 * Constants for search configuration
 */
export const SEARCH_CONSTANTS = {
  DEFAULT_LIMIT: 50,
  DEFAULT_SIMILARITY_THRESHOLD: 0.3,
  KEYWORD_FALLBACK_SIMILARITY: 0.5,
  EMBEDDING_DIMENSIONS: 1536,
  MAX_QUERY_LENGTH: 1000,
  MAX_RESULTS: 200,
} as const;

/**
 * Export all types for convenience
 * Phase 5: Removed EdgeFunctionSearchResult (deprecated)
 */
export type {
  Token,
  SearchResult,
  SearchMode,
  SearchResponse,
  SearchOptions,
  UserContext,
  EmbeddingVector,
  RPCSearchResult,
  SearchResultNormalizer,
};

